// // types/dashboard.ts
// import { useState, useCallback, useEffect, useRef } from "react";
// import useInfiniteScroll from "react-infinite-scroll-hook";
// import { DashboardPropsData } from "../interface/dashboard";
// import { useDispatch, useSelector } from "react-redux";
// import { RootState } from "../store/store";
// import {
//   setDashboard_items,
//   setRefresh_dashboard,
// } from "../store/contentSlice";

// // API response type for dashboard
// export interface DashboardApiResponse {
//   data: {
//     data: DashboardPropsData[];
//     meta: {
//       current_page: number;
//       last_page: number;
//     };
//   };
// }

// // Parameters type for dashboard fetch
// export interface DashboardFetchParams {
//   page: number;
//   page_size: number;
// }

// // Props type for dashboard infinite scroll
// export interface DashboardInfiniteScrollProps {
//   fetchData: (params: DashboardFetchParams) => Promise<DashboardApiResponse>;
//   initialParams: DashboardFetchParams;
//   initialItems?: DashboardPropsData[];
//   skip?: boolean;
// }

// export function useDashboardInfiniteScroll({
//   fetchData,
//   initialParams,
//   initialItems = [],
// }: DashboardInfiniteScrollProps) {
//   // Local state for managing items and pagination
//   const [items, setItems] = useState<DashboardPropsData[]>(initialItems);
//   const [params, setParams] = useState<DashboardFetchParams>(initialParams);
//   const [hasMore, setHasMore] = useState(true);
//   const [loading, setLoading] = useState(false);

//   // Reference to track initial mount
//   const isInitialMount = useRef(true);

//   // Redux access
//   const dispatch = useDispatch();
//   const savedItems = useSelector(
//     (state: RootState) => state.content.dashboard_items
//   );
//   const refresh_dashboard = useSelector(
//     (state: RootState) => state.content.refresh_dashboard
//   );

//   // Initial data loading effect
//   useEffect(() => {
//     const loadInitialData = async () => {
//       // If we have saved items and this is the initial mount, use them
//       if (savedItems.length > 0 && isInitialMount.current) {
//         setItems(savedItems);
//         // Update params to reflect the correct page based on saved items
//         setParams((prev) => ({
//           ...prev,
//           page: Math.ceil(savedItems.length / prev.page_size),
//         }));
//         isInitialMount.current = false;
//         return;
//       }

//       // If no saved items or refresh is needed, fetch fresh data
//       if (isInitialMount.current || refresh_dashboard) {
//         await refetch();
//         isInitialMount.current = false;
//       }
//     };

//     loadInitialData();
//   }, [refresh_dashboard]);

//   // Function to fetch more data
//   const fetchMoreData = useCallback(async () => {
//     if (loading || !hasMore) return;

//     setLoading(true);
//     try {
//       // Fetch the next page
//       const nextPage = params.page + 1;
//       const response = await fetchData({
//         ...params,
//         page: nextPage,
//       });

//       const { data, meta } = response.data;

//       // Update items both locally and in Redux
//       const updatedItems = [...items, ...data];
//       setItems(updatedItems);
//       dispatch(setDashboard_items(updatedItems));

//       // Update pagination state
//       setParams((prev) => ({
//         ...prev,
//         page: nextPage,
//       }));
//       setHasMore(meta.current_page < meta.last_page);
//     } catch (error) {
//       console.error("Error fetching more dashboard data:", error);
//     } finally {
//       setLoading(false);
//     }
//   }, [fetchData, params, hasMore, loading, items, dispatch]);

//   // Function to refetch data from scratch
//   const refetch = useCallback(async () => {
//     setLoading(true);
//     try {
//       // Reset to initial parameters
//       const response = await fetchData(initialParams);
//       const { data, meta } = response.data;

//       // Update items both locally and in Redux
//       setItems(data);
//       dispatch(setDashboard_items(data));

//       // Reset pagination state
//       setParams({
//         ...initialParams,
//         page: 1,
//       });
//       setHasMore(meta.current_page < meta.last_page);

//       // Reset refresh flag in Redux
//       if (refresh_dashboard) {
//         dispatch(setRefresh_dashboard(false));
//       }
//     } catch (error) {
//       console.error("Dashboard refetch error:", error);
//     } finally {
//       setLoading(false);
//     }
//   }, [fetchData, initialParams, dispatch, refresh_dashboard]);

//   // Set up infinite scroll
//   const [sentryRef] = useInfiniteScroll({
//     loading,
//     hasNextPage: hasMore,
//     onLoadMore: fetchMoreData,
//     disabled: loading,
//     // Trigger loading when element is 200px from viewport
//     rootMargin: "200px",
//   });

//   return { items, sentryRef, loading, refetch };
// }


import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { DashboardPropsData } from '../interface/dashboard';


export interface DashboardApiResponse {
  data: {
    data: DashboardPropsData[];
    meta: {
      current_page: number;
      last_page: number;
    };
  };
}

// Parameters type for dashboard fetch
export interface DashboardFetchParams {
  page: number;
  page_size: number;
  search: string;
}

// Props type for dashboard infinite scroll
export interface DashboardInfiniteScrollProps {
  fetchData: (params: DashboardFetchParams) => Promise<DashboardApiResponse>;
  initialParams: DashboardFetchParams;
  initialItems?: DashboardPropsData[];
  skip?: boolean;
}

export function useDashboardInfiniteScroll({
  fetchData,
  initialParams,
}: {
  fetchData: (params: DashboardFetchParams) => Promise<DashboardApiResponse>;
  initialParams: DashboardFetchParams;
}) {
  const queryClient = useQueryClient();
  const observerRef = useRef<IntersectionObserver>();

  // **State for Search Query**
  const [searchTerm, setSearchTerm] = useState(initialParams.search || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

   // **Debounce search input**
   useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim()); // Trim spaces to avoid unnecessary API calls
    }, 500); // Adjust debounce delay as needed

    return () => clearTimeout(handler);
  }, [searchTerm]);


  // Set up infinite query
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    refetch,
    isLoading
  } = useInfiniteQuery({
    queryKey: ['dashboard', debouncedSearchTerm || "all"],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await fetchData({
        ...initialParams,
        page: pageParam,
        search: debouncedSearchTerm || "", // If empty, fetch everything
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.current_page < lastPage.meta.last_page) {
        return lastPage.meta.current_page + 1;
      }
      return undefined;
    },
    staleTime: 1000 * 60 * 5, // Consider data stale after 5 minutes
    gcTime: 1000 * 60 * 10,   // Keep unused data in cache for 10 minutes
  });

  // Function to invalidate query and trigger refresh
  const invalidateAndRefetch = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  // Set up infinite scroll
  const [sentryRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage: !!hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: false,
    rootMargin: '200px',
  });

  // Flatten pages data for easier consumption
  const items = data?.pages.flatMap(page => page.data) ?? [];

  // Function to handle real-time updates
  
  const handleRealtimeUpdate = (newItem: DashboardPropsData) => {
    queryClient.setQueryData(['dashboard'], (oldData: any) => {
      if (!oldData) return oldData;

      // Create new pages array with updated data
      const newPages = oldData.pages.map((page: any) => ({
        ...page,
        data: [newItem, ...page.data.filter((item: DashboardPropsData) => 
          item.id !== newItem.id
        )],
      }));

      return {
        ...oldData,
        pages: newPages,
      };
    });
  };

  return {
    items,
    sentryRef,
    loading: isLoading || isFetching,
    refetch: invalidateAndRefetch,
    handleRealtimeUpdate, // Expose this to handle real-time updates
    searchTerm,
    setSearchTerm, // Expose search state setter
  };
}
